import React from 'react';
import MuiModal from '../components/mui-modal';
import { Formik, Form } from 'formik';
import * as DotHocReducer from '../../../faetures/dot-hoc/dotHocSlide';
import { BSKT_DotHoc } from '../../../services/dot_hoc/DotHocModels';
import {
    Box,
    FormControl,
    FormLabel,
    TextField,
    Typography,
    FormHelperText,
    Stack
} from '@mui/material';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { useDispatch } from 'react-redux';
import { dotHocService } from '../../../services/dot_hoc/dotHocService';
import { NotifyHelper } from '../../../helpers/toast';
import Button from '../../../components-ui/button';

interface CreateOrEditDotHocModalProps {
    onClose: () => void;
}

const CreateOrEditDotHocModal = (props: CreateOrEditDotHocModalProps) => {
    const { onClose } = props;
    const { createOrEditModel } = useAppSelector((x) => x.dotHoc);
    const dispatch = useDispatch();

    const initValues: BSKT_DotHoc = {
        ID_dot_hoc: createOrEditModel.ID_dot_hoc,
        Dot: createOrEditModel.Dot,
        Nam_tuyen_sinh: createOrEditModel.Nam_tuyen_sinh,
        Ten_dot: createOrEditModel.Ten_dot,
        Tu_ngay: createOrEditModel.Tu_ngay,
        Den_ngay: createOrEditModel.Den_ngay,
        Ghi_chu: createOrEditModel.Ghi_chu
    };

    const isEdit = !!createOrEditModel.ID_dot_hoc && createOrEditModel.ID_dot_hoc > 0;

    const handleSubmit = async (values: BSKT_DotHoc) => {
        if (values.ID_dot_hoc === 0) {
            const result = await dotHocService.create(values);
            if (!result.is_success) {
                NotifyHelper.Error(
                    result.message && result.message !== ''
                        ? result.message
                        : 'Lỗi khi thêm mới đợt học'
                );
            } else {
                dispatch(DotHocReducer.insertSuccess(values));
                NotifyHelper.Success(
                    result.message && result.message !== ''
                        ? result.message
                        : 'Thêm mới đợt học thành công'
                );
            }
        } else {
            const result = await dotHocService.update(values);
            if (!result.is_success) {
                NotifyHelper.Error(
                    result.message && result.message !== ''
                        ? result.message
                        : 'Lỗi khi cập nhật đợt học'
                );
            } else {
                dispatch(DotHocReducer.updateSuccess(values));
                NotifyHelper.Success(
                    result.message && result.message !== ''
                        ? result.message
                        : 'Cập nhật đợt học thành công'
                );
            }
        }
    };

    const handleClose = () => {
        onClose();
    };

    return (
        <MuiModal
            isOpen={true}
            onClose={handleClose}
            title={isEdit ? 'Cập nhật đợt học' : 'Thêm mới đợt học'}
        >
            <Formik
                initialValues={initValues}
                onSubmit={async (values: BSKT_DotHoc, helper) => {
                    await handleSubmit(values);
                    helper.setSubmitting(false);
                    helper.resetForm();
                    handleClose();
                }}
                validateOnChange={false}
                validateOnBlur={false}
            >
                {({
                    values,
                    handleChange,
                    errors,
                    touched,
                    isSubmitting,
                    resetForm,
                    setSubmitting
                }) => {
                    return (
                        <Form>
                            {/* Mô tả nhỏ */}
                            <Box mb={2}>
                                <Typography variant="body2" color="text.secondary">
                                    Nhập thông tin đợt học, năm tuyển sinh và thời gian áp dụng.
                                    Các trường có dấu{' '}
                                    <span style={{ color: '#e53935' }}>*</span> là bắt buộc.
                                </Typography>
                            </Box>

                            {/* Khối form chính: grid 2 cột */}
                            <Box
                                sx={{
                                    borderRadius: 2,
                                    border: '1px solid #f0f0f0',
                                    p: 2.5,
                                    backgroundColor: '#fafafa',
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                                    columnGap: 2,
                                    rowGap: 2
                                }}
                            >
                                {/* Tên đợt học */}
                                <FormControl
                                    fullWidth
                                    size="small"
                                    sx={{ gridColumn: { xs: '1 / -1', md: '1 / -1' } }}
                                >
                                    <FormLabel>
                                        Tên đợt học <span className="text-danger">*</span>
                                    </FormLabel>
                                    <TextField
                                        value={values.Ten_dot}
                                        name="Ten_dot"
                                        onChange={handleChange}
                                        placeholder="Tên đợt học"
                                        size="small"
                                    />
                                    {touched.Ten_dot && errors.Ten_dot && (
                                        <FormHelperText>{errors.Ten_dot as string}</FormHelperText>
                                    )}
                                </FormControl>

                                {/* Đợt */}
                                <FormControl fullWidth size="small">
                                    <FormLabel>Đợt</FormLabel>
                                    <TextField
                                        placeholder="Số thứ tự đợt"
                                        type="number"
                                        value={values.Dot}
                                        name="Dot"
                                        onChange={handleChange}
                                        size="small"
                                    />
                                    {touched.Dot && errors.Dot && (
                                        <FormHelperText>{errors.Dot as string}</FormHelperText>
                                    )}
                                </FormControl>

                                {/* Năm tuyển sinh */}
                                <FormControl fullWidth size="small">
                                    <FormLabel>
                                        Năm tuyển sinh <span className="text-danger">*</span>
                                    </FormLabel>
                                    <TextField
                                        placeholder="Năm tuyển sinh"
                                        value={values.Nam_tuyen_sinh}
                                        name="Nam_tuyen_sinh"
                                        onChange={handleChange}
                                        size="small"
                                    />
                                    {touched.Nam_tuyen_sinh && errors.Nam_tuyen_sinh && (
                                        <FormHelperText>
                                            {errors.Nam_tuyen_sinh as string}
                                        </FormHelperText>
                                    )}
                                </FormControl>

                                {/* Từ ngày */}
                                <FormControl fullWidth size="small">
                                    <FormLabel>
                                        Từ ngày <span className="text-danger">*</span>
                                    </FormLabel>
                                    <TextField
                                        placeholder="Từ ngày"
                                        type="date"
                                        value={
                                            values.Tu_ngay
                                                ? values.Tu_ngay.toString().slice(0, 10)
                                                : ''
                                        }
                                        name="Tu_ngay"
                                        onChange={handleChange}
                                        size="small"
                                    />
                                    {touched.Tu_ngay && errors.Tu_ngay && (
                                        <FormHelperText>{errors.Tu_ngay as string}</FormHelperText>
                                    )}
                                </FormControl>

                                {/* Đến ngày */}
                                <FormControl fullWidth size="small">
                                    <FormLabel>
                                        Đến ngày <span className="text-danger">*</span>
                                    </FormLabel>
                                    <TextField
                                        placeholder="Đến ngày"
                                        type="date"
                                        value={
                                            values.Den_ngay
                                                ? values.Den_ngay.toString().slice(0, 10)
                                                : ''
                                        }
                                        name="Den_ngay"
                                        onChange={handleChange}
                                        size="small"
                                    />
                                    {touched.Den_ngay && errors.Den_ngay && (
                                        <FormHelperText>{errors.Den_ngay as string}</FormHelperText>
                                    )}
                                </FormControl>

                                {/* Ghi chú – full width */}
                                <FormControl
                                    fullWidth
                                    size="small"
                                    sx={{ gridColumn: { xs: '1 / -1', md: '1 / -1' } }}
                                >
                                    <FormLabel>Ghi chú</FormLabel>
                                    <TextField
                                        multiline
                                        rows={3}
                                        placeholder="Ghi chú"
                                        value={values.Ghi_chu}
                                        name="Ghi_chu"
                                        onChange={handleChange}
                                        size="small"
                                    />
                                </FormControl>
                            </Box>

                            {/* Footer buttons */}
                            <Box mt={2}>
                                <Stack
                                    direction="row"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    spacing={2}
                                >
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                    >
                                        <span style={{ color: '#e53935' }}>*</span> Trường bắt buộc
                                        nhập
                                    </Typography>

                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="flex-end"
                                        gap={1}
                                    >
                                        <Button
                                            variant="contained"
                                            type="submit"
                                            size="small"
                                            isLoading={isSubmitting}
                                        >
                                            {isEdit ? 'Cập nhật' : 'Lưu'}
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            color="error"
                                            size="small"
                                            onClick={() => {
                                                resetForm();
                                                setSubmitting(false);
                                                handleClose();
                                            }}
                                        >
                                            Đóng
                                        </Button>
                                    </Box>
                                </Stack>
                            </Box>
                        </Form>
                    );
                }}
            </Formik>
        </MuiModal>
    );
};

export default CreateOrEditDotHocModal;